import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { AgGridReact } from 'ag-grid-react';
import { PlacementDetailsTableService, PlacementUtils } from "../../../../placements";
import { AlertMessagesService, InlineEditDialogBox, MediaOpsService, OmniIconComponent, OmniTooltipNextComponent, Utils } from "../../../../../shared";
import CampaignDetailsActionMenuRenderer from "./campaignDetailsActionMenuRenderer";
import MediaOrderDetailsModal from "./mediaOrderDetailsModal";
import '../../../../../styles/mediaOrderDetailsModal.scss';
import MediaOrderDetailsFilter from "./mediaOrderDetailsFilter";
import SyncWithPrismaConfirmation from "./SyncWithPrismaConfirmation";

// Constants
const defaultColDef = {
    flex: 5,
    filter: false,
    resizable: true,
    wrapText: true,
    autoHeight: true,
    floatingFilter: false,
    suppressHeaderMenuButton: true,
};

const mediaOrdersColumnDefs = [
    {
        headerName: "Channel",
        field: "channelDisplayNameAggrid",
        resizable: true,
        wrapText: true,
        autoHeight: true,
        flex: 1.5,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
        sort: 'asc'
    },
    {
        headerName: "Supplier",
        field: "supplierDisplayNameAggrid",
        resizable: true,
        wrapText: true,
        autoHeight: true,
        flex: 1.5,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
    },
    {
        headerName: "Date Range",
        field: "dateRangeDisplayNameAggrid",
        resizable: true,
        wrapText: true,
        autoHeight: true,
        flex: 1.5,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
    },
    {
        headerName: "Ordered",
        field: "orderedDisplayNameAggrid",
        resizable: true,
        wrapText: true,
        autoHeight: true,
        flex: 1,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
        cellStyle: { textAlign: 'right' }
    },
    {
        headerName: "Actions",
        field: "actionsDisplayNameAggrid",
        resizable: true,
        wrapText: true,
        autoHeight: true,
        flex: 1.6,
        filter: false,
        floatingFilter: false,
        suppressFloatingFilterButton: false,
    }
];

const MediaOrdersTable = (params) => {
    // Refs
    const gridRef = useRef(null);
    const isInlineOverrideRef = useRef(false);

    // State
    const [openActionMenuId, setOpenActionMenuId] = useState(null);
    const [data, setData] = useState([]);
    const [highlightedRowId, setHighlightedRowId] = useState(null);
    const [columnDefs, setColumnDefs] = useState([]);
    const [taxonomyName, setTaxonomyName] = useState(null);
    const [selectedCampaignObj, setSelectedCampaignObj] = useState(null);
    const [isShowModal, setIsShowModal] = useState(false);
    const [taxonomyKeys, setTaxonomyKeys] = useState(null);
    const [taxonomyNameWithAbbrevation, setTaxonomyNameWithAbbrevation] = useState(null);
    const [taxonomySchema, setTaxonomySchema] = useState(null);
    const [editableData, setEditableData] = useState(null);
    const [editFormData, setEditFormData] = useState(null);
    const [taxonomySchemaArray, setTaxonomySchemaArray] = useState(null);
    const [isMediaOrderModalOpen, setIsMediaOrderModalOpen] = useState(false);
    const [selectedMediaOrder, setSelectedMediaOrder] = useState(null);
    const [gridApi, setGridApi] = useState(null);
    const [channelsListForFilter, setChannelsListForFilter] = useState([]);
    const [suppliersListForFilter, setSuppliersListForFilter] = useState([]);
    const [clientIdsListForFilter, setClientIdsListForFilter] = useState([]);
    const [productIdsListForFilter, setProductIdsListForFilter] = useState([]);
    const [showPrismaConfirmation, setShowPrismaConfirmation] = useState(false);
    const [totalOrdered, setTotalOrdered] = useState(null);
    const [totalOrderedLocal, setTotalOrderedLocal] = useState(null);

    // Cell Renderers
    const dateRangeRenderer = useCallback((params) => {
        if (!params.data) {
            return '-';
        }

        if (params.data.dataTypeForAggrid === 'publisher') {
            const startDate = params.data.publisher_start_date;
            const endDate = params.data.publisher_end_date;
            return `${Utils.formatDateObject(startDate)} - ${Utils.formatDateObject(endDate)}`;
        } else if (params.data.dataTypeForAggrid === 'campaign') {
            const startDate = params.data.start_date;
            const endDate = params.data.end_date;
            return `${Utils.formatDateObject(startDate)} - ${Utils.formatDateObject(endDate)}`;
        }
    }, []);

    const orderedDisplayNameAggridRenderer = useCallback((rendererParams) => {
        if (!rendererParams.data) {
            return '-';
        }

        if (rendererParams.data?.orderedDisplayNameAggrid || rendererParams.data?.orderedDisplayNameAggridLocal) {
            const isLocal = params?.systemParametersData?.data?.isLocal;
            if (isLocal) {
                const displayValue = Utils.getLocalCurrency(rendererParams?.data?.local_currency_name ? rendererParams?.data?.local_currency_name : 'USD', Number(rendererParams?.data?.orderedDisplayNameAggridLocal || 0))
                return (<span className="ordered-amount">{displayValue}</span>)
            } else {
                const displayValue = Utils.getLocalCurrency('USD', Number(rendererParams?.data?.orderedDisplayNameAggrid || 0))
                return (<span className="ordered-amount">{displayValue}</span>)
            }
        }

        return '-';

    }, []);

    const handleMediaOrderClick = useCallback((campaign) => {
        setSelectedMediaOrder(campaign);
        setIsMediaOrderModalOpen(true);
    }, []);

    const handleMediaOrderModalClose = useCallback(() => {
        setIsMediaOrderModalOpen(false);
        setSelectedMediaOrder(null);
    }, []);

    const handleChannelsEdit = useCallback((newChannels) => {
        // Handle channel edit save logic here
        // You can add API call or state update logic here
        if (selectedMediaOrder) {
            // Update the selected media order with new channels
            setSelectedMediaOrder({ ...selectedMediaOrder, channels: newChannels });
        }
    }, [selectedMediaOrder]);

    const channelRenderer = useCallback((rendererParams) => {
        if (rendererParams.data?.dataTypeForAggrid === 'total') {
            return '';
        }

        if (!rendererParams.data) {
            return '-';
        }

        if (rendererParams.data.dataTypeForAggrid === 'campaign') {
            const channels = rendererParams.data?.channelPublisherNameArray;

            if (!channels || !Array.isArray(channels) || channels.length === 0) {
                return '';
            }

            const visibleChannels = channels.slice(0, 2); // Show first 2 channels as pills
            const remainingCount = channels.length - 2;

            return (
                <div
                    style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    {visibleChannels.map((channel, index) => (
                        <span key={index} className="ad-serving-fee-pill-container">
                            <span className="ad-serving-fee-pill">
                                {channel}
                            </span>
                        </span>
                    ))}
                    {remainingCount > 0 && (
                        <span style={{ color: '#006A98 !important', fontWeight: '600 !important' }}>
                            +{remainingCount}
                        </span>
                    )}
                </div>
            );
        } else {
            return (
                <span className="ad-serving-fee-pill-container">
                    <span className="ad-serving-fee-pill">{rendererParams.data?.channelDisplayNameAggrid}</span>
                </span>
            )
        }

    }, [handleMediaOrderClick]);

    const toggleActionMenu = useCallback((id) => {
        setOpenActionMenuId(prevId => {
            return prevId === id ? null : id;
        });
    }, []);

    // Refresh cells when menu state changes
    useEffect(() => {
        if (gridRef?.current?.api) {
            gridRef.current.api.refreshCells({
                columns: ['actionsDisplayNameAggrid'],
                force: true
            });
        }
    }, [openActionMenuId]);

    const genrateCampaignTaxonomyName = useCallback(() => {
        let campaignTaxonomyName = "", campaignTaxonomyNameWithAbbrevation = "", taxonomySchema = "", taxonomyJson = [];
        let campaignObj = selectedCampaignObj;

        if (!campaignObj || !params.campaignTaxonomyData?.fields) {
            return;
        }

        params.campaignTaxonomyData.fields.forEach((field, index) => {
            let taxonomyField = {
                code: "NU",
                delimiter: field['delimiter_value'],
                id: field['id'] || null
            }
            if (field?.name?.toLowerCase() === 'channel') {
                const channels = campaignObj['channels'] || [];
                if (channels.length > 1) {
                    campaignTaxonomyName += 'MUL' + field['delimiter_value'];
                    campaignTaxonomyNameWithAbbrevation += 'MUL' + field['delimiter_value'];
                    taxonomyField.code = 'MUL';
                } else if (channels.length === 1) {
                    campaignTaxonomyName += channels[0]['channel_name'] || "NU";
                    campaignTaxonomyName += field['delimiter_value'];
                    campaignTaxonomyNameWithAbbrevation += channels[0]['channel_name'] || " ";
                    campaignTaxonomyNameWithAbbrevation += field['delimiter_value'];
                    taxonomyField.code = channels[0]['channel_name'] || "NU";
                } else {
                    campaignTaxonomyName += "NU" + field['delimiter_value'];
                    campaignTaxonomyNameWithAbbrevation += " " + field['delimiter_value'];
                }
                taxonomySchema += "Channel" + '|';
            } else {
                /* field_type =1 for Dropdown field
                  field_type=2 for FreeText field
                  field_type= 9 for Static Text
                */

                switch (field.field_type) {
                    case 1:
                        let fieldCodeValue, fieldName = "";
                        fieldCodeValue = field.fielditems.filter(fielditem => fielditem['name'] === campaignObj[field?.name?.toLowerCase()]).map((fielditem) => {
                            fieldName = fielditem.name || "";
                            return fielditem.code;
                        })
                        campaignTaxonomyName += (fieldCodeValue[0] || "NU") + field['delimiter_value']
                        campaignTaxonomyNameWithAbbrevation += fieldName + field['delimiter_value']
                        taxonomyField.code = fieldCodeValue[0] || 'NU';
                        break;
                    case 2:
                        campaignTaxonomyName += (campaignObj[field?.name?.toLowerCase()] || "NU") + field['delimiter_value']
                        campaignTaxonomyNameWithAbbrevation += campaignObj[field?.name?.toLowerCase()] + field['delimiter_value']
                        taxonomyField.code = campaignObj[field?.name?.toLowerCase()] || "NU";
                        break;
                    case 9:
                        campaignTaxonomyName += field.static_field_value + field['delimiter_value']
                        campaignTaxonomyNameWithAbbrevation += field.static_field_value + field['delimiter_value']
                        taxonomyField.code = field.static_field_value;
                        break;
                    case 10:
                        campaignTaxonomyName += (campaignObj[field?.name?.toLowerCase()] || "{TaxonomyValueID}") + field['delimiter_value']
                        campaignTaxonomyNameWithAbbrevation += campaignObj[field?.name?.toLowerCase()] + field['delimiter_value']
                        taxonomyField.code = campaignObj[field?.name?.toLowerCase()] || "{TaxonomyValueID}";
                        break;
                    default:
                        campaignTaxonomyName += (campaignObj[field?.name?.toLowerCase()] || "NU") + field['delimiter_value']
                        campaignTaxonomyNameWithAbbrevation += campaignObj[field?.name?.toLowerCase()] + field['delimiter_value']
                        taxonomyField.code = campaignObj[field?.name?.toLowerCase()] || "NU";
                        break;
                }
                taxonomySchema += field["name"] + '|';
            }

            taxonomyJson.push(taxonomyField);
            if (index === params.campaignTaxonomyData.fields.length - 1) {
                setTaxonomyName(campaignTaxonomyName.slice(0, -1));
                setTaxonomyNameWithAbbrevation(campaignTaxonomyNameWithAbbrevation.slice(0, -1));
                setTaxonomySchema(taxonomySchema.slice(0, -1));
            }
        });
    }, [selectedCampaignObj, params.campaignTaxonomyData]);

    const handleDescriptionClick = useCallback((data) => {
        setSelectedCampaignObj(data);
        if (data?.campaignTaxonomy?.split(':|')[0] !== "-" && data?.campaignTaxonomy?.split(':|')[0]) {
            setTaxonomyName(data?.campaignTaxonomy?.split(':|')[0]);
        } else {
            genrateCampaignTaxonomyName();
        }
    }, [genrateCampaignTaxonomyName]);

    /**
     * Gets the appropriate status icon based on export status
     * @param {string} exportStatus - The export status value
     * @returns {string} The icon ID for the status
     */
    const getStatusIcon = useCallback((exportStatus) => {
        if (!exportStatus) {
            return '';
        }

        if (exportStatus === 'Campaign Sent') {
            return 'omni:informative:connectionSuccess';
        }

        if (exportStatus.toLowerCase().includes('error')) {
            return 'omni:informative:connectionError';
        }

        if (exportStatus === 'Not Approved') {
            return 'omni:informative:connectionNone';
        }

        return '';
    }, []);

    const handleSyncToPrismaClick = useCallback((campaign) => {
        if (!campaign) {
            console.error('No campaign provided for Prisma sync');
            return;
        }
        setSelectedMediaOrder(campaign);
        setShowPrismaConfirmation(true);
    }, []);

    const cancelSyncWithPrisma = useCallback(() => {
        setShowPrismaConfirmation(false);
        setSelectedMediaOrder(null);
    }, []);

    /**
     * Filters out "Other Fees" publishers from channels
     * @param {Array} channels - Array of channel objects with publishers
     * @returns {Array} Channels with filtered publishers (immutable)
     */
    const getChannelsWithoutOtherFees = useCallback((channels) => {
        if (!channels?.length) {
            return [];
        }

        return channels.map(channel => ({
            ...channel,
            publishers: channel?.publishers?.filter(
                publisher => publisher.publisher_type !== 'Other Fees'
            ) || []
        }));
    }, []);

    const handleSyncWithPrisma = useCallback(() => {
        if (!selectedMediaOrder) {
            console.error('No media order selected');
            return;
        }

        params.setSpinnerCount(1);
        setShowPrismaConfirmation(false);

        const payload = {
            campaignTaxonomy: selectedMediaOrder.campaignTaxonomy?.split(':|')[0],
            campaignTaxonomyDisplay: selectedMediaOrder.campaign_taxonomy_display,
            taxonomySchema: selectedMediaOrder.taxonomySchema,
            userEmail: params.omniProfile?.email,
            channels: getChannelsWithoutOtherFees(selectedMediaOrder.channels)?.map(item => item.channel_name),
            isCurrencyLocal: params.systemParametersData?.data?.isLocal,
            campaignStartDate: selectedMediaOrder.startDate,
            campaignEndDate: selectedMediaOrder.endDate
        };

        const campaignId = encodeURIComponent(selectedMediaOrder.id);
        const campaignName = selectedMediaOrder.campaign_name;

        MediaOpsService.sendCampaignToMediaOcean(payload, campaignId, params.versionOneApi)
            .then((response) => {
                if (response.status.trim() === 'error') {
                    AlertMessagesService.showMultipleAlerts([{
                        type: 'danger',
                        retryBtnText: 'Retry',
                        retryBtnEvent: handleSyncWithPrisma,
                        title: 'Campaign is not sent to Media Ocean',
                        bodyMsgArr: [response.message]
                    }]);
                } else {
                    AlertMessagesService.showSuccessToastMessage(`Selected channel(s) for ${campaignName} are sent to Media Ocean`);
                    params.fetchCampaignDetails();
                    setSelectedMediaOrder(null);
                }
            })
            .catch((error) => {
                AlertMessagesService.showMultipleAlerts([{
                    type: 'danger',
                    retryBtnText: 'Retry',
                    retryBtnEvent: handleSyncWithPrisma,
                    title: 'Unable to send the campaign to the Media Ocean',
                    bodyMsgArr: [error?.response?.data?.error?.message]
                }]);
            })
            .finally(() => { params.setSpinnerCount(0); });
    }, [selectedMediaOrder, params, getChannelsWithoutOtherFees]);

    const actionsDisplayNameAggridRenderer = useCallback((props) => {
        if (!props.data || props.data.dataTypeForAggrid !== 'campaign') {
            return null;
        }

        // Get openActionMenuId from context instead of closure
        const isThisOpen = props.context?.openActionMenuId === props?.data?.id;
        const statusIconClass = getStatusIcon(props.data?.export_status);
        const navigateToPlacementDetails = () => {
            let state = { ...params.locationState, campaignObj: props.data };
            params.navigate(`/placementDetailsTable/${props.data?.id}`, { state });
        }

        return (
            <div className="action-buttons">

                <OmniTooltipNextComponent>
                    <OmniIconComponent className="is-size-2 actions-icon" icon-id={statusIconClass}></OmniIconComponent>
                    <div slot="content"> {props.data?.export_status} </div>
                </OmniTooltipNextComponent>

                <button className="secondary placements-button" onClick={navigateToPlacementDetails}>Placements</button>
                <CampaignDetailsActionMenuRenderer
                    {...props}
                    id={props?.node?.id + "actions"}
                    isOpen={isThisOpen}
                    onToggle={props.context?.toggleActionMenu}
                    campaign={props?.data}
                    handleDescriptionClick={handleDescriptionClick}
                    navigateToPlacementDetails={navigateToPlacementDetails}
                    handleMediaOrderClick={handleMediaOrderClick}
                    handleSyncToPrismaClick={handleSyncToPrismaClick}
                />
            </div>
        );
    }, [handleDescriptionClick, handleMediaOrderClick, handleSyncToPrismaClick, params.locationState, params.navigate]);

    const applyRenderers = useCallback((cols) => {
        const cellRenderers = {
            dateRangeDisplayNameAggrid: dateRangeRenderer,
            channelDisplayNameAggrid: channelRenderer,
            actionsDisplayNameAggrid: actionsDisplayNameAggridRenderer,
            orderedDisplayNameAggrid: orderedDisplayNameAggridRenderer,
        };

        const applyRenderersRecursive = (columns) => {
            return columns.map(col => {
                // if column has children → recurse
                if (col.children) {
                    return {
                        ...col,
                        children: applyRenderersRecursive(col.children),
                    };
                }

                // otherwise apply renderer if matched
                return cellRenderers[col.field]
                    ? { ...col, cellRenderer: cellRenderers[col.field] }
                    : col;
            });
        };

        return applyRenderersRecursive(cols);
    }, [dateRangeRenderer, channelRenderer, actionsDisplayNameAggridRenderer, orderedDisplayNameAggridRenderer]);

    useEffect(() => {
        let campaign = selectedCampaignObj;

        if (campaign) {
            let campaignTaxonomy = campaign.campaign_taxonomy.split(':|');
            let taxonomyJson = campaign?.taxonomy ? campaign?.taxonomy : null;
            let taxonomySchema = '';
            let taxonomySchemaArray = params.campaignTaxonomyData;
            let taxonomyData = taxonomySchemaArray?.fields;
            //if campaign is too old and there is taxonomy_ids_schema
            if (!taxonomyJson?.length && campaign?.taxonomy_ids_schema) {
                let taxonomyIds = campaign?.taxonomy_ids_schema.split('|').map(val => {
                    return val.split(' *')[1]
                });

                let newSchema = [];
                taxonomyIds.forEach((id, index) => {
                    let field = {};
                    field = taxonomyData.find(field => id == field.id)
                    let name = field?.name || field?.alias;
                    newSchema.push(name);
                });

                taxonomySchema = newSchema.join('|');
                campaign.taxonomy_schema = taxonomySchema;
            }
            let editFormData = getCampaignEditData();
            let isNameAndSchemaFieldMatch = PlacementDetailsTableService.compareTaxonomyAndData(params.campaignTaxonomyData?.fields, editFormData, campaign?.taxonomy_schema, taxonomyJson);
            if (!isNameAndSchemaFieldMatch) {
                isInlineOverrideRef.current = true;
            }
            campaign.syntheticId = campaign?.synthetic_id;
            const taxonomyKeys = {
                'schema': 'taxonomy_schema',
                'displayName': 'campaign_taxonomy_display',
                'taxonomyName': 'campaign_taxonomy',
                'syntheticId': campaignTaxonomy[1],
            }

            setTaxonomyKeys(taxonomyKeys);
            setEditableData(campaign);
            setEditFormData(editFormData);
            setTaxonomySchemaArray(params.campaignTaxonomyData);
            setIsShowModal(true);
        }

    }, [taxonomyName, selectedCampaignObj]);

    const getCampaignEditData = () => {
        let taxonomySchemaArray = params.campaignTaxonomyData;
        let campaign = selectedCampaignObj;
        let taxonomyJson = campaign?.taxonomy ? campaign?.taxonomy : null;
        let campaignTaxonomy = campaign.campaign_taxonomy.split(':|');
        let taxonomyName = campaignTaxonomy[0];
        let taxonomySchema = campaign?.taxonomy_schema ? campaign?.taxonomy_schema : null;
        let taxonomyData = taxonomySchemaArray?.fields;

        return PlacementDetailsTableService.splitPlacementStringIntoSchemaObject(taxonomyName, taxonomySchema, taxonomyData, '', null, taxonomyJson);
    }

    const closeModal = (payload) => {
        if (Object.keys(payload || {}).length > 0) {
            params.updateSavedScrollPositionRef({ y: window.scrollY });
            params.fetchCampaignDetails();
        }

        setIsShowModal(false);
        setSelectedCampaignObj(null);
        setTaxonomyName(null);
        setTaxonomyNameWithAbbrevation(null);
        setTaxonomySchema(null);
        isInlineOverrideRef.current = false;
        setTaxonomyKeys(null);
        setEditableData(null);
        setEditFormData(null);
        setTaxonomySchemaArray(null);
    }

    // Initialize column definitions once on mount
    useEffect(() => {
        setColumnDefs(applyRenderers(mediaOrdersColumnDefs));
    }, [applyRenderers]);

    /**
     * Processes planning publishers for a channel and matches them with ordered data
     * @param {Object} channel - Channel object containing publishers
     * @param {Object} campaignOrderedData - Ordered data for the campaign
     * @param {Object} campaign - Campaign object
     * @param {Set} matchedOrderedSuppliers - Set to track matched ordered suppliers
     * @param {Set} matchedOrderedChannels - Set to track matched ordered channels
     * @returns {Array} Array of processed publisher objects
     */
    const processPlanningPublishers = useCallback((channel, campaignOrderedData, campaign, matchedOrderedSuppliers, matchedOrderedChannels) => {
        const publishers = [];
        const channelPublishers = channel?.publishers || [];

        for (let publisherIndex = 0; publisherIndex < channelPublishers.length; publisherIndex++) {
            const publisher = { ...channelPublishers[publisherIndex] };
            publisher.dataTypeForAggrid = 'publisher';
            publisher.channelDisplayNameAggrid = channel?.channel_name;
            publisher.supplierDisplayNameAggrid = publisher?.publisher_name || '';

            // Match with ordered data if available
            if (campaignOrderedData) {
                const channelOrderedData = campaignOrderedData.child_campaign_details?.find(
                    item => item?.channel?.toLowerCase() === channel.channel_name?.toLowerCase()
                );

                if (channelOrderedData) {
                    matchedOrderedChannels.add(channel.channel_name.toLowerCase());

                    const publisherOrderedData = channelOrderedData.suppliers?.find(
                        item => item?.supplier?.toLowerCase() === publisher.publisher_name?.toLowerCase()
                    );

                    if (publisherOrderedData) {
                        publisher.orderedDisplayNameAggrid = publisherOrderedData.ordered_total;
                        publisher.orderedDisplayNameAggridLocal = publisherOrderedData.ordered_total_local;
                        publisher.local_currency_name = campaign.local_currency_name;

                        const supplierKey = `${channel.channel_name}|${publisherOrderedData.supplier}`.toLowerCase();
                        matchedOrderedSuppliers.add(supplierKey);
                    }
                }
            }

            publishers.push(publisher);
        }

        return publishers;
    }, []);

    /**
     * Adds missing ordered suppliers within an existing planning channel
     * @param {Object} channel - Channel object
     * @param {Object} campaignOrderedData - Ordered data for the campaign
     * @param {Object} campaign - Campaign object
     * @param {Set} matchedOrderedSuppliers - Set of already matched supplier keys
     * @returns {Array} Array of missing supplier objects
     */
    const addMissingOrderedSuppliersInChannel = useCallback((channel, campaignOrderedData, campaign, matchedOrderedSuppliers) => {
        const missingSuppliers = [];

        const channelOrderedData = campaignOrderedData.child_campaign_details?.find(
            item => item?.channel?.toLowerCase() === channel.channel_name?.toLowerCase()
        );

        if (channelOrderedData?.suppliers) {
            for (const orderedSupplier of channelOrderedData.suppliers) {
                const supplierKey = `${channel.channel_name}|${orderedSupplier.supplier}`.toLowerCase();

                if (!matchedOrderedSuppliers.has(supplierKey)) {
                    const missingPublisher = {
                        channelDisplayNameAggrid: channel.channel_name,
                        supplierDisplayNameAggrid: orderedSupplier.supplier || '',
                        dateRangeDisplayNameAggrid: '',
                        publisher_start_date: orderedSupplier.supplier_start_date || '',
                        publisher_end_date: orderedSupplier.supplier_end_date || '',
                        local_currency_name: campaign.local_currency_name,
                        orderedDisplayNameAggrid: orderedSupplier.ordered_total || '',
                        orderedDisplayNameAggridLocal: orderedSupplier.ordered_total_local || '',
                        dataTypeForAggrid: 'publisher',
                    };

                    missingSuppliers.push(missingPublisher);
                    matchedOrderedSuppliers.add(supplierKey);
                }
            }
        }

        return missingSuppliers;
    }, []);

    /**
     * Adds missing ordered channels (channels that exist in ordered data but not in planning)
     * @param {Object} campaignOrderedData - Ordered data for the campaign
     * @param {Object} campaign - Campaign object
     * @param {Set} matchedOrderedChannels - Set of already matched channel names
     * @param {Set} matchedOrderedSuppliers - Set to track matched supplier keys
     * @returns {Array} Array of missing channel supplier objects
     */
    const addMissingOrderedChannels = useCallback((campaignOrderedData, campaign, matchedOrderedChannels, matchedOrderedSuppliers) => {
        const missingChannelSuppliers = [];

        if (!campaignOrderedData.child_campaign_details?.length) {
            return missingChannelSuppliers;
        }

        const missingChannels = campaignOrderedData.child_campaign_details.filter(
            item => !matchedOrderedChannels.has(item.channel?.toLowerCase())
        );

        for (const missingChannel of missingChannels) {
            if (missingChannel?.suppliers) {
                for (const orderedSupplier of missingChannel.suppliers) {
                    const missingPublisher = {
                        channelDisplayNameAggrid: missingChannel.channel || '',
                        supplierDisplayNameAggrid: orderedSupplier.supplier || '',
                        dateRangeDisplayNameAggrid: '',
                        publisher_start_date: orderedSupplier.supplier_start_date || '',
                        publisher_end_date: orderedSupplier.supplier_end_date || '',
                        local_currency_name: campaign.local_currency_name,
                        orderedDisplayNameAggrid: orderedSupplier.ordered_total || '',
                        orderedDisplayNameAggridLocal: orderedSupplier.ordered_total_local || '',
                        dataTypeForAggrid: 'publisher',
                    };

                    missingChannelSuppliers.push(missingPublisher);

                    const supplierKey = `${missingChannel.channel}|${orderedSupplier.supplier}`.toLowerCase();
                    matchedOrderedSuppliers.add(supplierKey);
                }
            }
        }

        return missingChannelSuppliers;
    }, []);

    /**
     * Aggregates ordered totals from nested publisher data to campaign level
     * @param {Object} campaign - Campaign object with nestedData array
     * @returns {Object} Campaign with aggregated ordered totals
     */
    const updateCampaignOrderedData = useCallback((campaign) => {
        if (!campaign?.nestedData?.length) {
            return campaign;
        }

        let totalOrdered = 0;
        let totalOrderedLocal = 0;

        campaign.nestedData.forEach(publisher => {
            totalOrdered += Number(publisher?.orderedDisplayNameAggrid || 0);
            totalOrderedLocal += Number(publisher?.orderedDisplayNameAggridLocal || 0);
        });

        campaign.orderedDisplayNameAggrid = totalOrdered;
        campaign.orderedDisplayNameAggridLocal = totalOrderedLocal;

        return campaign;
    }, []);

    /**
     * Processes a single campaign to build nested data structure
     * @param {Object} campaign - Campaign object to process
     * @param {Object} orderedData - Ordered data containing child campaigns
     * @returns {Object} Processed campaign with nested data
     */
    const processCampaign = useCallback((campaign, orderedData) => {
        const processedCampaign = { ...campaign };

        const campaignOrderedData = orderedData?.child_campaigns?.find(
            item => item.child_campaign_id === campaign.synthetic_id
        );

        const channels = campaign?.channels || [];
        processedCampaign.prismaCampaign = campaign.parent_id
            ? campaign.campaign_taxonomy
            : campaign.campaign_display_name;
        processedCampaign.dataTypeForAggrid = 'campaign';

        const nestedData = [];
        let channelPublisherNameArray = [];

        // Track matched items to prevent duplicates
        const matchedOrderedSuppliers = new Set();
        const matchedOrderedChannels = new Set();

        // Process each channel
        for (let channelIndex = 0; channelIndex < channels.length; channelIndex++) {
            const channel = { ...channels[channelIndex] };
            channel.dataTypeForAggrid = 'channel';

            // Add planning publishers with their matched ordered data
            const planningPublishers = processPlanningPublishers(
                channel,
                campaignOrderedData,
                campaign,
                matchedOrderedSuppliers,
                matchedOrderedChannels
            );

            nestedData.push(...planningPublishers);

            // Build channel name list for display
            if (channel?.channel_name) {
                channelPublisherNameArray.push(channel.channel_name);
            }

            // Add ordered suppliers that don't exist in planning for this channel
            if (campaignOrderedData) {
                const missingSuppliers = addMissingOrderedSuppliersInChannel(
                    channel,
                    campaignOrderedData,
                    campaign,
                    matchedOrderedSuppliers
                );
                nestedData.push(...missingSuppliers);
            }
        }

        // Add completely missing channels (exist in ordered but not in planning)
        if (campaignOrderedData) {
            const missingChannelSuppliers = addMissingOrderedChannels(
                campaignOrderedData,
                campaign,
                matchedOrderedChannels,
                matchedOrderedSuppliers
            );
            nestedData.push(...missingChannelSuppliers);

            // Add missing channel names to channelPublisherNameArray
            const addedChannels = new Set();
            missingChannelSuppliers.forEach(supplier => {
                const channelName = supplier.channelDisplayNameAggrid;
                if (channelName && !addedChannels.has(channelName)) {
                    channelPublisherNameArray.push(channelName);
                    addedChannels.add(channelName);
                }
            });
        }

        processedCampaign.nestedData = nestedData;
        processedCampaign.channelPublisherNameArray = channelPublisherNameArray;

        return updateCampaignOrderedData(processedCampaign);
    }, [processPlanningPublishers, addMissingOrderedSuppliersInChannel, addMissingOrderedChannels, updateCampaignOrderedData]);

    /**
     * Main update function - builds complete data structure for the table
     */
    const updateData = useCallback(() => {
        const childCampaigns = params?.childCampaigns || [];

        if (!childCampaigns.length) {
            setData([]);
            return;
        }

        const processedData = childCampaigns.map(campaign =>
            processCampaign(campaign, params.orderedData)
        );

        // Collect all unique channels, suppliers for filter
        const channelSet = new Set();
        const supplierMap = new Map(); // Use Map for O(1) lookup and automatic deduplication
        const clientIdSet = new Set();
        const productIdSet = new Set();

        processedData.forEach(campaign => {

            // Collect client IDs
            if (campaign.clientid) {
                clientIdSet.add(campaign.clientid);
            }

            // Collect product IDs
            if (campaign.productid) {
                productIdSet.add(campaign.productid);
            }

            // Collect channels from channelPublisherNameArray
            if (campaign.channelPublisherNameArray?.length) {
                campaign.channelPublisherNameArray.forEach(channel => {
                    if (channel) channelSet.add(channel);
                });
            }

            // Collect suppliers from nestedData
            if (campaign.nestedData?.length) {
                campaign.nestedData.forEach(publisher => {
                    const supplier = publisher.supplierDisplayNameAggrid;
                    const channel = publisher.channelDisplayNameAggrid;

                    if (supplier) {
                        const key = `${supplier}|${channel}`;
                        if (!supplierMap.has(key)) {
                            supplierMap.set(key, {
                                id: supplier,
                                value: supplier,
                                channel: channel
                            });
                        }
                    }
                });
            }
        });

        setChannelsListForFilter(
            Array.from(channelSet, channel => ({ id: channel, value: channel }))
        );
        setSuppliersListForFilter(Array.from(supplierMap.values()));
        setClientIdsListForFilter(Array.from(clientIdSet, clientId => ({ id: clientId, value: clientId })));
        setProductIdsListForFilter(Array.from(productIdSet, productId => ({ id: productId, value: productId })));

        // Calculate totals across all campaigns
        let totalOrdered = 0;
        let totalOrderedLocal = 0;

        processedData.forEach(campaign => {
            totalOrdered += Number(campaign?.orderedDisplayNameAggrid || 0);
            totalOrderedLocal += Number(campaign?.orderedDisplayNameAggridLocal || 0);
        });

        setTotalOrdered(totalOrdered);
        setTotalOrderedLocal(totalOrderedLocal);
        setData(processedData);
    }, [params?.childCampaigns, params.orderedData, processCampaign]);


    useEffect(() => {
        updateData();
    }, [updateData]);

    // Add gridContext - memoize to prevent unnecessary re-renders
    const gridContext = useMemo(() => ({
        isLocal: params?.systemParametersData?.data?.isLocal,
        campaignObj: params?.campaignObj,
        openActionMenuId: openActionMenuId,
        toggleActionMenu: toggleActionMenu,
        // Add any other context data needed by cell renderers
    }), [params?.systemParametersData?.data?.isLocal, params?.campaignObj, openActionMenuId, toggleActionMenu]);

    const isGroupOpenByDefault = useCallback((tableParams) => {
        const parentCampaign = params.locationState?.parentCampaign;
        return Boolean(
            parentCampaign &&
            Object.keys(parentCampaign).length > 0 &&
            params.locationState?.campaignObj?.id === tableParams.rowNode?.data?.id
        );
    }, [params.locationState?.parentCampaign, params.locationState?.campaignObj?.id]);

    const getRowClass = useCallback((params) => {
        if (params.data?.id === 'total-row') {
            return 'total-row';
        }
        if (params.data?.id === highlightedRowId) {
            return 'highlight-row';
        }
        return '';
    }, [highlightedRowId]);

    const onFirstDataRendered = useCallback(() => {
        const parentCampaign = params.locationState?.parentCampaign;
        const targetCampaignId = params.locationState?.campaignObj?.id;

        // Only need targetCampaignId to scroll - parentCampaign is optional (used for group expansion)
        if (!targetCampaignId) {
            return;
        }

        // Use setTimeout to allow the grid to fully render before scrolling
        setTimeout(() => {
            let targetNode = null;

            gridRef.current?.api?.forEachNode((node) => {
                if (node.data?.id === targetCampaignId) {
                    targetNode = node;
                    // If it's a group row with a parent campaign, ensure it's expanded first
                    if (node.group && !node.expanded && parentCampaign && Object.keys(parentCampaign).length > 0) {
                        node.setExpanded(true);
                    }
                }
            });

            // After finding the node, scroll to it
            if (targetNode) {
                // Wait for DOM to update after potential expansion
                setTimeout(() => {
                    // Search the entire document for the row
                    // Row IDs follow pattern: ${id}-chan-${random} or ${id}-pub-${random}
                    const allRows = document.querySelectorAll('[row-id]');
                    let rowDomElement = null;
                    const expectedId = `campaign-${targetCampaignId}`;

                    for (const row of allRows) {
                        const rowId = row.getAttribute('row-id');
                        if (rowId === expectedId || rowId?.includes(expectedId)) {
                            rowDomElement = row;
                            break;
                        }
                    }

                    if (rowDomElement && !params?.mediaOrdersRenderRef?.current) {
                        rowDomElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        params.updateMediaOrdersRenderRef();
                    }
                    // Set the highlighted row ID for persistent styling via getRowClass
                    setHighlightedRowId(targetCampaignId);
                }, 300);
            }
        }, 100);
    }, [params.locationState?.parentCampaign, params.locationState?.campaignObj?.id]);

    const onGridReady = useCallback((params) => {
        setGridApi(params.api);
    }, []);

    const applyFilter = useCallback((options) => {
        // Early return if no filter options
        if (!options || Object.keys(options).length === 0) {
            if (gridRef.current?.api) {
                gridRef.current.api.setGridOption('rowData', data);
            }
            return;
        }

        // Pre-compute filter dates once (outside the loop)
        const filterStartDate = options.startDate ? new Date(options.startDate).setHours(0, 0, 0, 0) : null;
        const filterEndDate = options.endDate ? new Date(options.endDate).setHours(23, 59, 59, 999) : null;

        const filteredData = data.filter(campaign => {
            // Non-campaign rows (like totals) always pass through
            const isCampaignRow = campaign.dataTypeForAggrid === 'campaign';

            // Start date filter
            if (filterStartDate !== null) {
                const campaignStartDate = campaign.start_date ? new Date(campaign.start_date).getTime() : null;
                if (!campaignStartDate || campaignStartDate < filterStartDate) {
                    return false;
                }
            }

            // End date filter
            if (filterEndDate !== null) {
                const campaignEndDate = campaign.end_date ? new Date(campaign.end_date).getTime() : null;
                if (!campaignEndDate || campaignEndDate > filterEndDate) {
                    return false;
                }
            }

            // Channel filter
            if (options.channels) {
                const campaignChannels = campaign.channelPublisherNameArray || [];
                if (!campaignChannels.includes(options.channels)) {
                    return false;
                }
            }

            // Supplier filter (skip for non-campaign rows)
            if (options.suppliers && isCampaignRow) {
                const suppliers = campaign.nestedData?.map(p => p.supplierDisplayNameAggrid) || [];
                if (!suppliers.includes(options.suppliers)) {
                    return false;
                }
            }

            // Client code filter (skip for non-campaign rows)
            if (options.clientCode && isCampaignRow) {
                if (campaign.clientid !== options.clientCode) {
                    return false;
                }
            }

            // Product code filter (skip for non-campaign rows)
            if (options.productCode && isCampaignRow) {
                if (campaign.productid !== options.productCode) {
                    return false;
                }
            }

            return true;
        });

        // Update grid with filtered data
        if (gridRef.current?.api) {
            gridRef.current.api.setGridOption('rowData', filteredData);
        }
    }, [data]);

    return (
        <div className="media-orders-table">

            {isMediaOrderModalOpen && selectedMediaOrder && (
                <MediaOrderDetailsModal
                    isOpen={isMediaOrderModalOpen}
                    onClose={handleMediaOrderModalClose}
                    syncIcon={getStatusIcon(selectedMediaOrder?.export_status)}
                    integrationStatus="Prisma"
                    campaignName={selectedMediaOrder.prismaCampaign}
                    dateRange={selectedMediaOrder.start_date && selectedMediaOrder.end_date
                        ? `${Utils.formatDateObject(selectedMediaOrder.start_date)} - ${Utils.formatDateObject(selectedMediaOrder.end_date)}`
                        : '-'}
                    prismaId={selectedMediaOrder.mediaocean_id}
                    clientCode={selectedMediaOrder.clientid}
                    productCode={selectedMediaOrder.productid}
                    lastModified={selectedMediaOrder.updated_at
                        ? Utils.formatDateObject(selectedMediaOrder.updated_at)
                        : '-'}
                    lastModifiedBy={selectedMediaOrder?.updated_by?.[0]?.toUpperCase() || ''}
                    channels={selectedMediaOrder.channelPublisherNameArray}
                    onEditChannels={handleChannelsEdit}
                />
            )}

            {isShowModal && editableData &&
                <InlineEditDialogBox
                    omniProfile={params.omniProfile}
                    taxonomySchemaArray={params.campaignTaxonomyData}
                    campaignObj={selectedCampaignObj}
                    taxonomyMap={null}
                    allTaxonomyData={null}
                    versionOneApi={params.versionOneApi}
                    systemParametersData={params.systemParametersData}
                    setSpinnerCount={params.setSpinnerCount}
                    displayInlineEditModal={isShowModal}
                    onInlineModalClose={closeModal}
                    taxonomyKeys={taxonomyKeys}
                    selectedRowData={editableData}
                    editFormData={editFormData}
                    isOverride={isInlineOverrideRef.current}
                    isCreatingChildCampaign={false}
                />
            }

            {gridApi && <MediaOrderDetailsFilter
                channelsListForFilter={channelsListForFilter}
                suppliersListForFilter={suppliersListForFilter}
                clientIdsListForFilter={clientIdsListForFilter}
                productIdsListForFilter={productIdsListForFilter}
                onApplyFilter={applyFilter}
                clearFilters={() => updateData()}
                gridApi={gridApi} />}

            <SyncWithPrismaConfirmation
                isOpen={showPrismaConfirmation}
                onCancel={cancelSyncWithPrisma}
                onDontSync={cancelSyncWithPrisma}
                onSync={handleSyncWithPrisma}
            />

            {data.length > 0 && <AgGridReact
                className="media-orders-aggrid-table"
                ref={gridRef}
                columnDefs={columnDefs}
                rowData={data}
                treeData={true}
                treeDataChildrenField={"nestedData"}
                groupDefaultExpanded={0}
                suppressContextMenu={true}
                autoGroupColumnDef={{
                    headerName: "Prisma Campaign",
                    field: "prismaCampaign",
                    cellRenderer: 'agGroupCellRenderer',
                    cellRendererParams: {
                        suppressCount: true,
                        innerRenderer: (params) => {

                            // Handle completely missing data
                            if (!params.data) {
                                return '';
                            }

                            // Handle valid data - return plain text only
                            const description = params.data.description || params.value || '';
                            const isChannel = !params.data.publisher_name;

                            const handleClick = () => {
                                handleDescriptionClick(params.data);
                            };

                            if (isChannel) {
                                return (
                                    <span
                                        onClick={handleClick}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {description}
                                    </span>
                                );
                            } else {
                                return (
                                    <span
                                        onClick={handleClick}
                                        style={{ cursor: 'pointer', paddingLeft: '16px' }}
                                    >
                                        {description}
                                    </span>
                                );
                            }
                        }
                    },
                    flex: 3
                }}
                getRowId={(params) => {
                    // Use stable IDs based on data properties instead of random IDs
                    if (params?.data?.dataTypeForAggrid === 'campaign') {
                        return `campaign-${params?.data?.id}`;
                    } else if (params?.data?.dataTypeForAggrid === 'publisher') {
                        return `publisher-${params?.data?.channelDisplayNameAggrid}-${params?.data?.supplierDisplayNameAggrid}-${params?.data?.publisher_name || ''}`;
                    }
                    return `row-${params?.data?.id || params?.data?.channelDisplayNameAggrid || ''}-${params?.data?.supplierDisplayNameAggrid || ''}`;
                }}
                defaultColDef={defaultColDef}
                sideBar={false}
                context={gridContext}
                domLayout="autoHeight"
                isGroupOpenByDefault={isGroupOpenByDefault}
                onFirstDataRendered={onFirstDataRendered}
                getRowClass={getRowClass}
                onGridReady={onGridReady}
            />}

            {data.length > 0 && (totalOrdered > 0 || totalOrderedLocal > 0) && (
                <div className="media-orders-footer">
                    <div className="footer-col footer-campaign"></div>
                    <div className="footer-col footer-channel"></div>
                    <div className="footer-col footer-supplier"></div>
                    <div className="footer-col footer-date-range"></div>
                    <div className="footer-col footer-ordered">
                        {Utils.getLocalCurrency(
                            params?.systemParametersData?.data?.isLocal
                                ? (data[0]?.local_currency_name || 'USD')
                                : 'USD',
                            params?.systemParametersData?.data?.isLocal ? totalOrderedLocal : totalOrdered
                        )}
                    </div>
                    <div className="footer-col footer-actions"></div>
                </div>
            )}
        </div>
    );
}

export default MediaOrdersTable;
export { defaultColDef, mediaOrdersColumnDefs };
